/**
 * ============================================================
 * USE DIALOG LAB — Hook para integração de modais com o Lab
 * ============================================================
 * Cada modal pode chamar este hook passando seu data-dialog-id.
 * O hook registra/desregistra automaticamente o diálogo no
 * LabContext conforme ele abre e fecha.
 *
 * DESIGN: Aceita null como dialogId (no-op seguro). O hook
 * é tolerante a contexto ausente (ex: modais em páginas públicas
 * fora do LabContextProvider).
 *
 * @example
 * // Em AlunoMatriculaDialog.tsx:
 * useDialogLab('modal-novo-aluno', isOpen);
 *
 * // No componente Modal central (com guard de null):
 * useDialogLab(dialogId ?? null, open);
 */

import { useEffect } from 'react';
import { useLabContextSafe } from '../LabContext';

/**
 * Registra um diálogo no sistema Lab para descoberta contextual.
 *
 * @param dialogId - ID único do diálogo (deve estar em LAB_DIALOG_MAP) ou null (no-op)
 * @param isOpen - Estado de abertura do diálogo
 */
export function useDialogLab(dialogId: string | null, isOpen: boolean): void {
  const lab = useLabContextSafe();

  useEffect(() => {
    if (!dialogId || !lab) return;

    const { registerDialog, unregisterDialog } = lab;

    if (isOpen) {
      registerDialog(dialogId);
    } else {
      unregisterDialog(dialogId);
    }

    return () => {
      unregisterDialog(dialogId);
    };
  }, [dialogId, isOpen, lab]);
}

export default useDialogLab;
